/**
 * Failover Monitor
 * 
 * Continuously monitors server health.
 * Prevents flapping by using thresholds and backoffs.
 * Automatically transitions networkState to the appropriate Local mode when the server fails,
 * and handles recovery back to SERVER_CONNECTED.
 */

import { networkState, NetworkMode } from "../networkState";
import { peerManager } from "../transport/peerManager";

export const PING_INTERVAL_MS = 5000;
// While the server is away, ask far more often. Live logs 22-24 Oct: after a
// Wi-Fi drop a pad needed 3 good pings 5 s apart (15-25 s) before it sent what
// it was holding, even with Wi-Fi already back. Now ~3 s.
export const AWAY_PING_INTERVAL_MS = 1500;
const FAILURE_THRESHOLD = 2; // Number of consecutive failed pings before declaring OFFLINE
export const RECOVERY_THRESHOLD = 2; // Number of consecutive successful pings before declaring CONNECTED
const REQUEST_TIMEOUT_MS = 3000;

class FailoverMonitor {
  constructor() {
    this.consecutiveFailures = 0;
    this.consecutiveSuccesses = 0;
    this.monitorInterval = null;
  }

  start() {
    if (this.monitorInterval) return;
    this.monitorInterval = true;
    this._schedule();
    // The phone saying its Wi-Fi is back is a reason to ask now, not on the
    // next tick.
    this._onOnline = () => this.checkHealth();
    if (typeof window !== "undefined") window.addEventListener("online", this._onOnline);
  }

  stop() {
    clearTimeout(this._timer);
    this._timer = null;
    this.monitorInterval = null;
    if (this._onOnline && typeof window !== "undefined") window.removeEventListener("online", this._onOnline);
    this._onOnline = null;
  }

  /** Next ping: every 5 s while connected, every 1.5 s while the server is away. */
  _schedule() {
    if (!this.monitorInterval) return;
    clearTimeout(this._timer);
    const away = networkState.getMode() !== NetworkMode.SERVER_CONNECTED;
    this._timer = setTimeout(async () => {
      await this.checkHealth();
      this._schedule();
    }, away ? AWAY_PING_INTERVAL_MS : PING_INTERVAL_MS);
  }

  async checkHealth() {
    // One ping at a time: the "online" event can land while a tick is asking.
    if (this._checking) return;
    this._checking = true;
    let isHealthy;
    try {
      isHealthy = await this.pingServer();
    } finally {
      this._checking = false;
    }

    if (isHealthy) {
      this.consecutiveFailures = 0;
      this.consecutiveSuccesses++;

      if (this.consecutiveSuccesses >= RECOVERY_THRESHOLD) {
        this.recoverToServer();
      }
    } else {
      this.consecutiveSuccesses = 0;
      this.consecutiveFailures++;

      if (this.consecutiveFailures === 1 && networkState.getMode() === NetworkMode.SERVER_CONNECTED) {
        networkState.setMode(NetworkMode.SERVER_DEGRADED);
      } else if (this.consecutiveFailures >= FAILURE_THRESHOLD) {
        this.failoverToMesh();
      }
    }
  }

  async pingServer() {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      
      const baseUrl = process.env.REACT_APP_BACKEND_URL || "";
      const res = await fetch(`${baseUrl}/api/realtime/health`, {
        signal: controller.signal,
        cache: 'no-store'
      });
      
      clearTimeout(timeoutId);
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  failoverToMesh() {
    const peersCount = peerManager.getConnectedPeers().length;
    if (peersCount > 0) {
      networkState.setMode(NetworkMode.MESH_ACTIVE);
    } else {
      // Still attempt connection or declare LOCAL_ONLY
      if (peerManager.signalingSocket?.readyState === WebSocket.OPEN) {
        networkState.setMode(NetworkMode.MESH_CONNECTING);
      } else {
        networkState.setMode(NetworkMode.LOCAL_ONLY);
      }
    }
  }

  recoverToServer() {
    const currentMode = networkState.getMode();
    if (currentMode !== NetworkMode.SERVER_CONNECTED && currentMode !== NetworkMode.RECONCILING) {
      // Enter reconciling state first
      networkState.setMode(NetworkMode.RECONCILING);
      
      // Let the syncService handle reconciliation. Once done, it will set SERVER_CONNECTED.
      // We emit an event or call syncService explicitly here.
      if (this.onRecoveryRequested) {
        this.onRecoveryRequested();
      } else {
        // Fallback if no hook
        networkState.setMode(NetworkMode.SERVER_CONNECTED);
      }
    }
  }
}

export const failoverMonitor = new FailoverMonitor();
